<template>
    <div
        ref="rootEl"
        class="modal-container modal-container-dialog"
        :class="[containerClass, { hidden: !display, closing: closing }]"
        tabindex="-1"
        role="dialog"
        :aria-hidden="!display"
        @keydown="keyDownHandle"
        @animationend="onAnimationEnd"
        @mousedown.self="onMouseDown"
        @touchstart.self="onTouchOutside"
    >
        <slot></slot>
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";
import { FocusTrap } from "@/utils/focus-trap";

defineOptions({ name: "ModalDialogContainer" });

const display = defineModel<boolean>("display");

const {
    lockClose = false,
    static: isStatic = false,
    closeCallback = undefined,
    containerClass = "",
} = defineProps<{
    lockClose?: boolean;
    static?: boolean;
    closeCallback?: (done: () => void) => void;
    containerClass?: string;
}>();

const emit = defineEmits<{
    key: [e: KeyboardEvent];
    close: [];
}>();

const closing = ref(false);
const rootEl = useTemplateRef<HTMLElement>("rootEl");
let focusTrap: FocusTrap | null = null;

const close = (forced?: boolean) => {
    if (lockClose && forced !== true) {
        return;
    }
    if (closeCallback && forced !== true) {
        closeCallback(() => {
            emit("close");
            closing.value = true;
        });
    } else {
        emit("close");
        closing.value = true;
    }
};

const onAnimationEnd = (e: AnimationEvent) => {
    e.stopPropagation();
    if (e.animationName === "modal-close-animation") {
        closing.value = false;
        display.value = false;
    }
};

const keyDownHandle = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Escape" && display.value && !closing.value && !isStatic) {
        close();
    } else {
        emit("key", e);
    }
};

const onTouchOutside = (e: TouchEvent) => {
    e.stopPropagation();
    if (!isStatic) {
        close();
    }
};

const onMouseDown = (e: MouseEvent) => {
    e.stopPropagation();
    if (e.button === 0 && !isStatic) {
        close();
    }
};

const focusLost = () => {
    if (display.value && !closing.value) {
        rootEl.value?.focus();
    }
};

defineExpose({ close });

onMounted(() => {
    if (rootEl.value) {
        focusTrap = new FocusTrap(rootEl.value, focusLost);
        if (display.value) {
            focusTrap.activate();
        }
    }
});

onBeforeUnmount(() => {
    if (focusTrap) {
        focusTrap.destroy();
    }
});

watch(display, (isDisplayed) => {
    if (isDisplayed) {
        closing.value = false;
        if (focusTrap) {
            focusTrap.activate();
        }
    } else if (focusTrap) {
        focusTrap.deactivate();
    }
});
</script>
